import type {
  ConfigChange,
  EngineConfig,
  EngineStats,
  ExportOptions,
  PhysicalOptions,
  PhysicalPlan,
  PrepareStats,
  PreprocessSettings,
  RenderSettings,
  SourceMetadata,
  StepInfo,
  StopReason,
} from '@/types/engine';

export type WorkerRequest =
  | { type: 'initialize'; id: number }
  | { type: 'loadTarget'; id: number; rgba: Uint8Array; width: number; height: number; config: EngineConfig }
  | { type: 'preview'; id: number; rgba: Uint8Array; width: number; height: number; preprocess: PreprocessSettings }
  | { type: 'configure'; id: number; config: EngineConfig }
  | { type: 'generatePins'; id: number }
  | { type: 'prepare'; id: number }
  | { type: 'start'; id: number }
  | { type: 'pause'; id: number }
  | { type: 'cancel'; id: number }
  | { type: 'step'; id: number }
  | { type: 'replay'; id: number; sequence: Uint16Array }
  | { type: 'reset'; id: number }
  | { type: 'snapshot'; id: number }
  | { type: 'export'; id: number; format: string; render: RenderSettings; options: ExportOptions }
  | { type: 'render'; id: number; width: number; height: number; render: RenderSettings }
  | { type: 'toProject'; id: number; source: SourceMetadata; render: RenderSettings }
  | { type: 'applyProject'; id: number; json: string }
  | { type: 'physicalPlan'; id: number; options: PhysicalOptions }
  | { type: 'stats'; id: number }
  | { type: 'dispose'; id: number };

export type RequestType = WorkerRequest['type'];

export interface SolutionSnapshot {
  sequence: Uint16Array;
  scores: Float32Array;
  errors: Float32Array;
  current: Uint8Array;
  target: Uint8Array;
  width: number;
  height: number;
  pins: Float32Array;
  stats: EngineStats;
}

export type WorkerResponse =
  | { type: 'initialized'; id: number; version: string }
  | { type: 'targetLoaded'; id: number; width: number; height: number; processed: Uint8Array }
  | { type: 'preview'; id: number; gray: Uint8Array; width: number; height: number }
  | { type: 'configured'; id: number; change: ConfigChange }
  | { type: 'pins'; id: number; pins: Float32Array }
  | { type: 'prepared'; id: number; stats: PrepareStats; pins: Float32Array; target: Uint8Array }
  | { type: 'started'; id: number }
  | { type: 'paused'; id: number; iteration: number; error: number }
  | { type: 'cancelled'; id: number; iteration: number; error: number }
  | { type: 'stepped'; id: number; step: StepInfo; current: Uint8Array }
  | { type: 'replayed'; id: number; iteration: number; error: number; current: Uint8Array; currentPin: number }
  | { type: 'resetDone'; id: number }
  | { type: 'snapshot'; id: number; snapshot: SolutionSnapshot }
  | { type: 'exported'; id: number; format: string; text: string }
  | { type: 'rendered'; id: number; rgba: Uint8Array; width: number; height: number }
  | { type: 'project'; id: number; json: string }
  | { type: 'projectApplied'; id: number; pins: Float32Array; sequence: Uint16Array; scores: Float32Array; errors: Float32Array; iteration: number; error: number; current: Uint8Array; target: Uint8Array; config: EngineConfig }
  | { type: 'physicalPlan'; id: number; plan: PhysicalPlan }
  | { type: 'stats'; id: number; stats: EngineStats }
  | { type: 'disposed'; id: number }
  | { type: 'error'; id: number | null; message: string };

export interface ProgressMessage {
  type: 'progress';
  iteration: number;
  maxStrings: number;
  error: number;
  currentPin: number;
  newLines: Uint16Array;
  newScores: Float32Array;
  newErrors: Float32Array;
  current: Uint8Array | null;
  stepsPerSecond: number;
  elapsedMs: number;
  batchSize: number;
  wasmMemoryBytes: number;
}

export interface CompleteMessage {
  type: 'complete';
  reason: StopReason;
  iteration: number;
  error: number;
  elapsedMs: number;
  current: Uint8Array;
}

export type WorkerEvent = ProgressMessage | CompleteMessage;
export type WorkerMessage = WorkerResponse | WorkerEvent;

export function transferablesOf(message: object): ArrayBuffer[] {
  const buffers: ArrayBuffer[] = [];
  const seen = new Set<ArrayBuffer>();
  const visit = (value: unknown): void => {
    if (ArrayBuffer.isView(value)) {
      const buffer = value.buffer;
      const full = value.byteOffset === 0 && value.byteLength === buffer.byteLength;
      if (full && buffer instanceof ArrayBuffer && !seen.has(buffer)) {
        seen.add(buffer);
        buffers.push(buffer);
      }
      return;
    }
    if (value && typeof value === 'object') {
      for (const child of Object.values(value as Record<string, unknown>)) {
        visit(child);
      }
    }
  };
  visit(message);
  return buffers;
}

export function isResponse(message: WorkerMessage): message is WorkerResponse {
  return 'id' in message;
}

export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}
